import React from 'react';
import { UserProgress, ModuleContent } from '../types';
import { MODULES } from '../constants';
import { PieChart, Trophy, AlertCircle, CheckCircle, ArrowRight } from 'lucide-react';

interface ProgressDashboardProps {
  progress: UserProgress;
  navigateToModule: (m: ModuleContent) => void;
}

export const ProgressDashboard: React.FC<ProgressDashboardProps> = ({ progress, navigateToModule }) => {
  const completedCount = progress.completedModules.length;
  const totalModules = MODULES.length;
  const progressPercent = Math.round((completedCount / totalModules) * 100);

  // Identify weak areas (score < 80%)
  const weakModules = MODULES.filter(m => {
    const score = progress.moduleScores[m.id];
    return score !== undefined && score < 80;
  });

  return (
    <div className="max-w-5xl mx-auto p-6 md:p-12">
      <h2 className="text-3xl font-bold mb-8 text-cyber-dark flex items-center gap-3">
        <PieChart className="text-purple-600" /> Learner Dashboard
      </h2>

      {/* Top Stats Cards */}
      <div className="grid md:grid-cols-3 gap-6 mb-12">
        <div className="bg-white p-6 rounded-xl shadow border-l-4 border-purple-500">
          <p className="text-slate-500 font-bold text-sm uppercase">Modules Completed</p>
          <div className="flex items-end gap-2">
            <span className="text-4xl font-bold text-slate-800">{completedCount}</span>
            <span className="text-slate-400 mb-1">/ {totalModules}</span>
          </div>
          <div className="w-full bg-slate-100 h-2 rounded-full mt-3">
            <div className="bg-purple-500 h-2 rounded-full transition-all duration-1000" style={{width: `${progressPercent}%`}}></div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow border-l-4 border-yellow-500">
          <p className="text-slate-500 font-bold text-sm uppercase">Exam Status</p>
          <div className="flex items-center gap-2 mt-1">
            {progress.assessmentScore !== null ? (
              <>
                <Trophy className={progress.assessmentScore >= 70 ? "text-yellow-500" : "text-slate-300"} size={32} />
                <span className="text-3xl font-bold text-slate-800">{progress.assessmentScore}%</span>
              </>
            ) : (
              <span className="text-2xl font-bold text-slate-400 italic">Not taken</span>
            )}
          </div>
          <p className="text-xs text-slate-500 mt-3">{progress.assessmentScore !== null && progress.assessmentScore >= 70 ? "Passed - Great job!" : "Take the final exam to earn your badge."}</p>
        </div>

        <div className="bg-white p-6 rounded-xl shadow border-l-4 border-blue-500">
          <p className="text-slate-500 font-bold text-sm uppercase">Learning Path</p>
          <p className="mt-2 font-bold text-slate-800">{progressPercent === 100 ? "Complete!" : "In Progress"}</p>
          <p className="text-xs text-slate-500 mt-1">Keep going to unlock the final assessment.</p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        {/* Module Scores List */}
        <div className="bg-white rounded-xl shadow overflow-hidden">
          <div className="bg-slate-50 p-4 border-b">
            <h3 className="font-bold text-slate-700">Module Performance</h3>
          </div>
          <div className="p-4 space-y-4">
            {MODULES.map(m => {
              const score = progress.moduleScores[m.id];
              return (
                <div key={m.id} className="flex items-center justify-between border-b last:border-0 pb-3 last:pb-0">
                  <div className="flex items-center gap-3">
                    {score !== undefined ? (
                      <CheckCircle className="text-green-500" size={20} />
                    ) : (
                      <div className="w-5 h-5 rounded-full border-2 border-slate-200"></div>
                    )}
                    <span className={score !== undefined ? "text-slate-800" : "text-slate-400"}>{m.title}</span>
                  </div>
                  {score !== undefined && (
                    <span className={`font-bold px-2 py-1 rounded text-sm ${score >= 80 ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>
                      {Math.round(score)}%
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Areas to Revisit */}
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow overflow-hidden">
            <div className="bg-slate-50 p-4 border-b">
              <h3 className="font-bold text-slate-700 flex items-center gap-2">
                 <AlertCircle size={18} className="text-orange-500" /> Areas to Revisit
              </h3>
            </div>
            <div className="p-6">
              {weakModules.length > 0 ? (
                <div className="space-y-4">
                  <p className="text-sm text-slate-600">You scored below 80% on these modules. Consider reviewing them:</p>
                  {weakModules.map(m => (
                    <button 
                      key={m.id}
                      onClick={() => navigateToModule(m)}
                      className="w-full flex justify-between items-center p-3 bg-orange-50 border border-orange-200 rounded-lg hover:bg-orange-100 transition-colors text-left"
                    >
                      <span className="font-bold text-orange-800">{m.title}</span>
                      <ArrowRight size={16} className="text-orange-400" />
                    </button>
                  ))}
                </div>
              ) : progressPercent === 0 ? (
                <p className="text-slate-500 italic text-center">Start learning to see recommendations.</p>
              ) : (
                <div className="text-center py-6">
                  <CheckCircle size={48} className="text-green-500 mx-auto mb-2" />
                  <p className="text-green-800 font-bold">All caught up!</p>
                  <p className="text-green-600 text-sm">Your scores are looking great.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
